import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Pause, SkipBack, SkipForward, Scissors, Check, X,
  ChevronLeft, ChevronRight, Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

/**
 * VideoTrimEditor - A professional video trimming component
 * Features:
 * - Visual timeline with draggable trim handles
 * - Frame-by-frame navigation
 * - Preview trimmed section
 * - Keyboard shortcuts
 */
const VideoTrimEditor = ({ 
  videoUrl, 
  trimStart = 0, 
  trimEnd = null, 
  onTrimChange,
  onCaptureThumbnail,
  userId 
}) => {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [localTrimStart, setLocalTrimStart] = useState(trimStart);
  const [localTrimEnd, setLocalTrimEnd] = useState(trimEnd);
  const [isDragging, setIsDragging] = useState(null); // 'start', 'end', or 'playhead'
  const [previewMode, setPreviewMode] = useState(false);

  // Initialize trim values
  useEffect(() => {
    setLocalTrimStart(trimStart || 0);
    setLocalTrimEnd(trimEnd);
  }, [trimStart, trimEnd]);

  // Handle video metadata loaded
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      if (!localTrimEnd) {
        setLocalTrimEnd(dur);
      }
      videoRef.current.currentTime = localTrimStart || 0;
    }
  };

  // Update current time display
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      
      // In preview mode, loop within trim range
      if (previewMode && localTrimEnd && time >= localTrimEnd) {
        videoRef.current.currentTime = localTrimStart || 0;
      }
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        if (previewMode) {
          videoRef.current.currentTime = localTrimStart || 0;
        }
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Seek to specific time
  const seekTo = (time) => {
    if (videoRef.current) {
      const clampedTime = Math.max(0, Math.min(duration, time));
      videoRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    }
  };

  // Skip forward/backward
  const skip = (seconds) => {
    seekTo(currentTime + seconds);
  };

  // Frame navigation (assuming 30fps)
  const skipFrames = (frames) => {
    seekTo(currentTime + (frames / 30));
  };

  // Format time to MM:SS.ms
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00.00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  // Calculate position percentage
  const getPositionPercent = (time) => {
    return duration > 0 ? (time / duration) * 100 : 0;
  };

  // Handle timeline click
  const handleTimelineClick = (e) => {
    if (!timelineRef.current || isDragging) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = percent * duration;
    seekTo(time);
  };

  // Handle drag start for trim handles
  const handleDragStart = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(type);
  };

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = percent * duration;
    
    if (isDragging === 'start') {
      const newStart = Math.min(time, (localTrimEnd || duration) - 0.1);
      setLocalTrimStart(Math.max(0, newStart));
    } else if (isDragging === 'end') {
      const newEnd = Math.max(time, localTrimStart + 0.1);
      setLocalTrimEnd(Math.min(duration, newEnd));
    } else if (isDragging === 'playhead') {
      seekTo(time);
    }
  }, [isDragging, duration, localTrimEnd, localTrimStart]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
  }, []);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Apply trim changes - actually trim the video using FFmpeg backend
  const applyTrim = async () => {
    if (!videoUrl) {
      toast.error('No video to trim');
      return;
    }
    
    const trimEnd = localTrimEnd || duration;
    
    if (localTrimStart >= trimEnd) {
      toast.error('Start time must be before end time');
      return;
    }
    
    if (trimEnd - localTrimStart < 0.5) {
      toast.error('Trimmed video must be at least 0.5 seconds');
      return;
    }
    
    if (!userId) {
      toast.error('User ID is required for trimming');
      return;
    }
    
    toast.loading('Trimming video... This may take a moment.', { id: 'trim' });
    
    try {
      console.log('[TRIM] Sending trim request:', {
        video_url: videoUrl,
        trim_start: localTrimStart,
        trim_end: trimEnd,
        user_id: userId
      });
      
      const response = await fetch(`${API_URL}/api/moodboard/vault/trim-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_url: videoUrl,
          trim_start: localTrimStart,
          trim_end: trimEnd,
          user_id: userId
        })
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.trimmed_url) {
        toast.dismiss('trim');
        toast.success('Video trimmed successfully!');
        
        // Update the shot with the new trimmed video URL and duration
        const trimmedDuration = trimEnd - localTrimStart;
        const mins = Math.floor(trimmedDuration / 60);
        const secs = Math.floor(trimmedDuration % 60);
        const durationEnd = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        
        console.log('[TRIM] Success, new URL:', data.trimmed_url);
        
        onTrimChange({
          video_url: data.trimmed_url,
          trim_start: 0,
          trim_end: null,
          duration_start: '00:00',
          duration_end: durationEnd
        });
      } else {
        toast.dismiss('trim');
        const errorMsg = data.detail || 'Failed to trim video. Please try again.';
        console.error('[TRIM] Error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[TRIM] Exception:', error);
      toast.dismiss('trim');
      toast.error('Failed to trim video. Check console for details.');
    }
  };

  // Reset trim
  const resetTrim = () => {
    setLocalTrimStart(0);
    setLocalTrimEnd(duration);
    onTrimChange({
      trim_start: 0,
      trim_end: duration || null
    });
    toast.success('Trim reset');
  };

  // Set trim start to current position
  const setStartHere = () => {
    const newStart = currentTime;
    setLocalTrimStart(newStart);
    onTrimChange({
      trim_start: newStart,
      trim_end: localTrimEnd || duration
    });
    toast.success(`Start set to ${formatTime(newStart)}`);
  };

  // Set trim end to current position
  const setEndHere = () => {
    const newEnd = currentTime;
    setLocalTrimEnd(newEnd);
    onTrimChange({
      trim_start: localTrimStart,
      trim_end: newEnd
    });
    toast.success(`End set to ${formatTime(newEnd)}`);
  };

  // Preview trimmed section
  const togglePreview = () => {
    setPreviewMode(!previewMode);
    if (!previewMode) {
      seekTo(localTrimStart);
      if (!isPlaying) {
        videoRef.current?.play();
        setIsPlaying(true);
      }
    }
  };

  // Capture current frame as thumbnail
  const captureFrame = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      
      const formData = new FormData();
      formData.append('file', blob, 'thumbnail.jpg');
      formData.append('user_id', userId);
      
      try {
        toast.loading('Capturing thumbnail...', { id: 'capture' });
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          onCaptureThumbnail(data.url);
          toast.dismiss('capture');
          toast.success('Thumbnail captured!');
        }
      } catch (error) {
        toast.dismiss('capture');
        toast.error('Failed to capture thumbnail');
      }
    }, 'image/jpeg', 0.9);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skip(e.shiftKey ? -5 : -1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skip(e.shiftKey ? 5 : 1);
          break;
        case ',':
          e.preventDefault();
          skipFrames(-1);
          break;
        case '.':
          e.preventDefault();
          skipFrames(1);
          break;
        case 'i':
          e.preventDefault();
          setStartHere();
          break;
        case 'o':
          e.preventDefault();
          setEndHere();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTime, isPlaying]);

  const trimStartPercent = getPositionPercent(localTrimStart);
  const trimEndPercent = getPositionPercent(localTrimEnd || duration);
  const currentPercent = getPositionPercent(currentTime);
  const trimDuration = (localTrimEnd || duration) - localTrimStart;

  return (
    <div className="bg-black/80 rounded-xl border border-white/20 overflow-hidden">
      {/* Video Preview */}
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          muted={isMuted}
          playsInline
        />
        
        {/* Play/Pause overlay */}
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
        >
          <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
            {isPlaying ? (
              <Pause size={28} className="text-white" fill="white" />
            ) : (
              <Play size={28} className="text-white ml-1" fill="white" />
            )}
          </div>
        </button>
        
        {/* Preview mode indicator */}
        {previewMode && (
          <div className="absolute top-3 left-3 bg-green-500/80 text-white text-xs px-2 py-1 rounded">
            PREVIEW MODE
          </div>
        )}
        
        {/* Time display */}
        <div className="absolute bottom-3 left-3 bg-black/70 text-white text-sm px-2 py-1 rounded font-mono">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        
        {/* Mute button */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="absolute bottom-3 right-3 p-2 bg-black/70 rounded hover:bg-black/90"
        >
          {isMuted ? (
            <VolumeX size={18} className="text-white" />
          ) : (
            <Volume2 size={18} className="text-white" />
          )}
        </button>
      </div>
      
      {/* Timeline */}
      <div className="p-4 space-y-4">
        {/* Timeline Scrubber */}
        <div className="space-y-2">
          <div 
            ref={timelineRef}
            className="relative h-12 bg-white/5 rounded-lg cursor-pointer select-none"
            onClick={handleTimelineClick}
          >
            {/* Trim region background */}
            <div 
              className="absolute top-0 bottom-0 bg-blue-500/30 rounded"
              style={{
                left: `${trimStartPercent}%`,
                width: `${trimEndPercent - trimStartPercent}%`
              }}
            />
            
            {/* Excluded regions (darker) */}
            <div 
              className="absolute top-0 bottom-0 left-0 bg-black/50 rounded-l"
              style={{ width: `${trimStartPercent}%` }}
            />
            <div 
              className="absolute top-0 bottom-0 right-0 bg-black/50 rounded-r"
              style={{ width: `${100 - trimEndPercent}%` }}
            />
            
            {/* Trim start handle */}
            <div
              className="absolute top-0 bottom-0 w-3 bg-green-500 cursor-ew-resize hover:bg-green-400 transition-colors flex items-center justify-center rounded-l"
              style={{ left: `calc(${trimStartPercent}% - 6px)` }}
              onMouseDown={handleDragStart('start')}
            >
              <div className="w-0.5 h-6 bg-white/50 rounded" />
            </div>
            
            {/* Trim end handle */}
            <div
              className="absolute top-0 bottom-0 w-3 bg-red-500 cursor-ew-resize hover:bg-red-400 transition-colors flex items-center justify-center rounded-r"
              style={{ left: `calc(${trimEndPercent}% - 6px)` }}
              onMouseDown={handleDragStart('end')}
            >
              <div className="w-0.5 h-6 bg-white/50 rounded" />
            </div>
            
            {/* Playhead */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white cursor-ew-resize"
              style={{ left: `${currentPercent}%` }}
              onMouseDown={handleDragStart('playhead')}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow" />
            </div>
          </div>
          
          {/* Time labels */}
          <div className="flex justify-between text-xs text-white/50 font-mono">
            <span>{formatTime(0)}</span>
            <span className="text-green-400">{formatTime(localTrimStart)}</span>
            <span className="text-white">Trim: {formatTime(trimDuration)}</span>
            <span className="text-red-400">{formatTime(localTrimEnd || duration)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-between">
          {/* Playback controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => skip(-5)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded"
              title="Skip -5s"
            >
              <SkipBack size={18} />
            </button>
            <button
              onClick={() => skipFrames(-1)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded"
              title="Previous frame (,)"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={togglePlay}
              className="p-3 bg-white/10 text-white rounded-full hover:bg-white/20"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
            <button
              onClick={() => skipFrames(1)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded"
              title="Next frame (.)"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => skip(5)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded"
              title="Skip +5s"
            >
              <SkipForward size={18} />
            </button>
          </div>
          
          {/* Trim controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={setStartHere}
              className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded text-sm hover:bg-green-500/30 flex items-center gap-1"
              title="Set trim start (I)"
            >
              <span className="text-xs opacity-60">[</span> Set Start
            </button>
            <button
              onClick={setEndHere}
              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30 flex items-center gap-1"
              title="Set trim end (O)"
            >
              Set End <span className="text-xs opacity-60">]</span>
            </button>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={togglePreview}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${
                previewMode 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Play size={14} />
              Preview
            </button>
            <button
              onClick={captureFrame}
              className="px-3 py-1.5 bg-white/10 text-white rounded text-sm hover:bg-white/20"
              title="Use current frame as thumbnail"
            >
              📷 Capture
            </button>
          </div>
        </div>
        
        {/* Apply/Reset */}
        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <button
            onClick={resetTrim}
            className="px-4 py-2 text-white/60 hover:text-white text-sm"
          >
            Reset
          </button>
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-xs">
              Shortcuts: Space=Play, I=Start, O=End, ,/.=Frame
            </span>
            <button
              onClick={applyTrim}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 flex items-center gap-2"
            >
              <Check size={16} />
              Apply Trim
            </button>
          </div>
        </div>
      </div>
      
      {/* Hidden canvas for thumbnail capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default VideoTrimEditor;
